import React, { useEffect, useState } from 'react';
import apiService from '../../../services/apiService';
import CompletedDeliveries from './CompletedDeliveries';

interface SalesRepStat {
  repId: string;
  repName: string;
  totalAssigned: number;
  contacted: number;
  converted: number;
  avgResponseTimeMin: number;
  conversionRate: number;
  efficiencyScore?: number;
  ratingBadge?: string;
  ratingColor?: string;
}

const STATUS_COLORS: Record<string, string> = {
  'New Lead':       '#3B82F6', // Blue
  'Contacted':      '#10B981', // Green
  'Follow-up':      '#F59E0B', // Amber
  'Interested':     '#8B5CF6', // Purple
  'Booked':         '#059669', // Emerald
  'Not Interested': '#EF4444', // Red
  'Converted':      '#10B981',
};

const SOURCE_COLORS = [
  '#3B82F6', '#10B981', '#8B5CF6', '#F59E0B', '#EC4899', '#6366F1', '#14B8A6'
];

let reportsCache: any = null;

const CRMReports: React.FC = () => {
  const [loading, setLoading] = useState(!reportsCache);
  const [sortBy, setSortBy] = useState<'efficiency' | 'conversion' | 'speed' | 'volume'>('efficiency');
  const [activeTab, setActiveTab] = useState<'analytics' | 'deliveries'>('analytics');
  const [activeChartTab, setActiveChartTab] = useState<'overview' | 'status' | 'sources' | 'performance'>('overview');

  const [reportData, setReportData] = useState<any>(reportsCache || {
    totalLeads: 0,
    conversionRate: 0,
    convertedLeads: 0,
    totalBookings: 0,
    totalDeliveries: 0,
    avgResponseTime: 0,
    statusBreakdown: {},
    sourceBreakdown: {},
    salesPerformance: [],
    trendData: [],
    slaBreakdown: {
      instant: 0,    // <= 15m
      standard: 0,   // 15m - 1h
      delayed: 0,    // 1h - 24h
      breach: 0,     // > 24h
      uncontacted: 0
    }
  });

  const fetchReports = async (silent = false) => {
    try {
      if (!silent && !reportsCache) setLoading(true);
      const [statsRes, distStatsRes] = await Promise.all([
        apiService.getOverviewStats(),
        apiService.getLeadDistributionStats()
      ]);
      
      if (statsRes.success && distStatsRes.success) {
        const statsData = statsRes.data;
        const backendStats = distStatsRes.data || [];
        
        // Calculate conversion rate
        const converted = statsData.stats.convertedLeads;
        const totalL = statsData.stats.totalLeads;
        const conversionRate = totalL ? ((converted / totalL) * 100).toFixed(1) : 0;
        
        // Processed sales stats
        const processedStats: SalesRepStat[] = backendStats.map((rep: any) => {
          const convPoints = rep.conversionRate * 0.6;
          let speedPoints = 0;
          if (rep.totalAssigned > 0 && rep.contacted > 0) {
            const t = rep.avgResponseTimeMin;
            if (t <= 15) speedPoints = 40;
            else if (t <= 30) speedPoints = 35;
            else if (t <= 60) speedPoints = 30;
            else if (t <= 120) speedPoints = 20;
            else speedPoints = 10;
          }
          const efficiencyScore = Math.round(convPoints + speedPoints);
          
          let ratingBadge = 'Standard';
          let ratingColor = 'var(--crm-text-secondary)';
          if (efficiencyScore >= 80) {
            ratingBadge = '🏆 Elite';
            ratingColor = '#7C3AED';
          } else if (efficiencyScore >= 50) {
            ratingBadge = '⭐ Top Performer';
            ratingColor = 'var(--crm-info)';
          }

          return {
            ...rep,
            efficiencyScore,
            ratingBadge,
            ratingColor
          };
        });

        const trendData = Object.entries(statsData.last7DaysMap || {}).map(([day, count]) => ({ day, count }));

        const finalData = {
          totalLeads: statsData.stats.totalLeads,
          totalCustomers: statsData.stats.totalCustomers,
          convertedLeads: converted,
          totalBookings: statsData.stats.showroomBookings,
          totalDeliveries: statsData.stats.showroomDeliveries || 0,
          conversionRate,
          avgResponseTime: statsData.slaStats.avgResponseTime,
          statusBreakdown: statsData.statusMap,
          sourceBreakdown: statsData.sourceMap,
          salesPerformance: processedStats,
          trendData,
          slaBreakdown: statsData.slaStats.breakdown
        };

        setReportData(finalData);
        reportsCache = finalData;
      }
    } catch (err) {
      console.error('Failed to generate reports', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!reportsCache) {
      fetchReports();
    } else {
      setLoading(false);
    }
  }, []);

  const getSortedPerformance = () => {
    const perf = [...(reportData.salesPerformance || [])];
    return perf.sort((a: SalesRepStat, b: SalesRepStat) => {
      if (sortBy === 'efficiency') return (b.efficiencyScore || 0) - (a.efficiencyScore || 0);
      if (sortBy === 'conversion') return b.conversionRate - a.conversionRate;
      if (sortBy === 'speed') {
        if (a.avgResponseTimeMin === 0 && b.avgResponseTimeMin > 0) return 1;
        if (b.avgResponseTimeMin === 0 && a.avgResponseTimeMin > 0) return -1;
        return a.avgResponseTimeMin - b.avgResponseTimeMin;
      }
      if (sortBy === 'volume') return b.totalAssigned - a.totalAssigned;
      return 0;
    });
  };

  if (loading) {
    return (
      <div className="crm-loading">
        <div className="crm-spinner"></div>
        Generating visual reports & graphs...
      </div>
    );
  }

  const sortedPerformance = getSortedPerformance();
  const sla = reportData.slaBreakdown;
  const totalResponded = sla.instant + sla.standard + sla.delayed + sla.breach;

  // Donut Chart Data preparation
  const statusItems = Object.entries(reportData.statusBreakdown || {})
    .filter(([_, count]: any) => count > 0)
    .map(([status, count]: any) => ({
      status,
      count,
      color: STATUS_COLORS[status] || '#94A3B8',
      percentage: reportData.totalLeads ? ((count / reportData.totalLeads) * 100).toFixed(1) : '0'
    }));

  const radius = 60;
  const circumference = 2 * Math.PI * radius;
  let accumulatedPercent = 0;

  // Max count for trend graph scaling
  const maxTrendCount = Math.max(...reportData.trendData.map((d: any) => d.count), 5);

  if ((activeTab as string) === 'deliveries') {
    return <CompletedDeliveries onBack={() => setActiveTab('analytics')} />;
  }

  const isDeliveriesTab = (activeTab as string) === 'deliveries';
  const isAnalyticsTab = (activeTab as string) === 'analytics';

  return (
    <div>
      <div className="crm-page-header">
        <div className="crm-page-header-left">
          <h2>Analytics & Visual Reports</h2>
          <p>Real-time visual graphs, conversion metrics, lead trends, and team analytics</p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button className="crm-btn crm-btn-ghost" onClick={() => fetchReports(false)}>↻ Refresh Data</button>
        </div>
      </div>

      {/* Reports Sub-Navigation Tabs */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', borderBottom: '2px solid var(--crm-border-color, #E5E7EB)', paddingBottom: '12px' }}>
        <button
          onClick={() => setActiveTab('analytics')}
          style={{
            padding: '10px 18px',
            borderRadius: '8px',
            border: 'none',
            background: isAnalyticsTab ? 'var(--crm-primary, #3B82F6)' : 'var(--crm-bg-card, #F3F4F6)',
            color: isAnalyticsTab ? '#ffffff' : 'var(--crm-text-primary, #374151)',
            fontWeight: 700,
            fontSize: '14px',
            cursor: 'pointer',
            boxShadow: isAnalyticsTab ? '0 2px 8px rgba(59, 130, 246, 0.3)' : 'none',
            transition: 'all 0.2s ease'
          }}
        >
          📊 General Analytics & Reports
        </button>

        <button
          onClick={() => setActiveTab('deliveries')}
          style={{
            padding: '10px 18px',
            borderRadius: '8px',
            border: 'none',
            background: isDeliveriesTab ? '#10B981' : 'var(--crm-bg-card, #F3F4F6)',
            color: isDeliveriesTab ? '#ffffff' : 'var(--crm-text-primary, #374151)',
            fontWeight: 700,
            fontSize: '14px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            boxShadow: isDeliveriesTab ? '0 2px 8px rgba(16, 185, 129, 0.3)' : 'none',
            transition: 'all 0.2s ease'
          }}
        >
          🚚 Completed Deliveries Report
          <span style={{ 
            background: isDeliveriesTab ? 'rgba(255,255,255,0.25)' : '#D1FAE5', 
            color: isDeliveriesTab ? '#ffffff' : '#065F46', 
            padding: '2px 8px', 
            borderRadius: '12px', 
            fontSize: '12px', 
            fontWeight: 800 
          }}>
            {reportData.totalDeliveries || 0}
          </span>
        </button>
      </div>

      {/* Top Stat Cards */}
      <div className="crm-stats-grid">
        <div className="crm-stat-card" style={{ borderLeft: '4px solid var(--crm-primary)' }}>
          <div className="crm-stat-card-body">
            <h3>Conversion Rate</h3>
            <div className="stat-value">{reportData.conversionRate}%</div>
          </div>
        </div>
        <div className="crm-stat-card" style={{ borderLeft: '4px solid var(--crm-success)' }}>
          <div className="crm-stat-card-body">
            <h3>Converted / Booked</h3>
            <div className="stat-value">{reportData.convertedLeads}</div>
          </div>
        </div>
        <div className="crm-stat-card" style={{ borderLeft: '4px solid #6366F1' }}>
          <div className="crm-stat-card-body">
            <h3>Showroom Bookings</h3>
            <div className="stat-value">{reportData.totalBookings}</div>
          </div>
        </div>
        <div className="crm-stat-card" style={{ borderLeft: '4px solid #10B981' }}>
          <div className="crm-stat-card-body">
            <h3>Total Deliveries</h3>
            <div className="stat-value">{reportData.totalDeliveries}</div>
          </div>
        </div>
        <div className="crm-stat-card" style={{ borderLeft: '4px solid var(--crm-warning)' }}>
          <div className="crm-stat-card-body">
            <h3>Avg Response Speed</h3>
            <div className="stat-value">{reportData.avgResponseTime} <span style={{ fontSize: '14px', color: 'var(--crm-text-secondary)' }}>min</span></div>
          </div>
        </div>
        <div className="crm-stat-card" style={{ borderLeft: '4px solid var(--crm-info)' }}>
          <div className="crm-stat-card-body">
            <h3>Total Leads</h3>
            <div className="stat-value">{reportData.totalLeads}</div>
          </div>
        </div>
      </div>

      {/* GRAPH SECTION 1: Lead Inflow Trend & Status Donut Chart */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: '20px', marginTop: '20px' }}>
        
        {/* Graph 1: 7-Day Lead Inflow Trend Line Chart */}
        <div className="crm-table-wrapper" style={{ padding: '20px', background: 'var(--crm-bg-card)', borderRadius: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '15px', color: 'var(--crm-text-primary)' }}>📈 7-Day Lead Generation Trend</h3>
              <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: 'var(--crm-text-secondary)' }}>Daily volume of incoming leads</p>
            </div>
            <span style={{ fontSize: '12px', fontWeight: 600, background: 'rgba(59,130,246,0.1)', color: 'var(--crm-primary)', padding: '4px 10px', borderRadius: '12px' }}>
              Peak: {maxTrendCount} leads
            </span>
          </div>

          <div style={{ height: '180px', width: '100%', position: 'relative', marginTop: '10px' }}>
            <svg viewBox="0 0 500 150" style={{ width: '100%', height: '100%', overflow: 'visible' }}>
              <defs>
                <linearGradient id="trendGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.4" />
                  <stop offset="100%" stopColor="#3B82F6" stopOpacity="0.0" />
                </linearGradient>
              </defs>
              
              {/* Grid lines */}
              <line x1="0" y1="20" x2="500" y2="20" stroke="var(--crm-border)" strokeDasharray="3 3" opacity="0.5" />
              <line x1="0" y1="70" x2="500" y2="70" stroke="var(--crm-border)" strokeDasharray="3 3" opacity="0.5" />
              <line x1="0" y1="120" x2="500" y2="120" stroke="var(--crm-border)" strokeDasharray="3 3" opacity="0.5" />

              {/* Line path */}
              {(() => {
                const points = reportData.trendData.map((d: any, i: number) => {
                  const x = (i / (reportData.trendData.length - 1 || 1)) * 480 + 10;
                  const y = 130 - (d.count / maxTrendCount) * 110;
                  return { x, y, val: d.count, day: d.day };
                });

                const pathD = points.reduce((acc: string, pt: any, i: number) => 
                  i === 0 ? `M ${pt.x} ${pt.y}` : `${acc} L ${pt.x} ${pt.y}`, ''
                );

                const areaD = `${pathD} L ${points[points.length - 1]?.x || 490} 130 L ${points[0]?.x || 10} 130 Z`;

                return (
                  <>
                    <path d={areaD} fill="url(#trendGradient)" />
                    <path d={pathD} fill="none" stroke="#3B82F6" strokeWidth="3" strokeLinecap="round" />
                    {points.map((pt: any, idx: number) => (
                      <g key={idx}>
                        <circle cx={pt.x} cy={pt.y} r="5" fill="#3B82F6" stroke="white" strokeWidth="2" />
                        <text x={pt.x} y={pt.y - 10} textAnchor="middle" fontSize="11" fontWeight="bold" fill="var(--crm-text-primary)">
                          {pt.val}
                        </text>
                      </g>
                    ))}
                  </>
                );
              })()}
            </svg>
          </div>

          {/* Day X-axis labels */}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px', padding: '0 5px' }}>
            {reportData.trendData.map((d: any, idx: number) => (
              <span key={idx} style={{ fontSize: '11px', color: 'var(--crm-text-muted)', fontWeight: 500 }}>
                {d.day.split(',')[0]}
              </span>
            ))}
          </div>
        </div>

        {/* Graph 2: Interactive Status Distribution Donut Chart */}
        <div className="crm-table-wrapper" style={{ padding: '20px', background: 'var(--crm-bg-card)', borderRadius: '12px' }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '15px', color: 'var(--crm-text-primary)' }}>
            🍩 Lead Status Distribution
          </h3>

          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '20px' }}>
            {/* SVG Donut Chart */}
            <div style={{ position: 'relative', width: '160px', height: '160px', flexShrink: 0, margin: '0 auto' }}>
              <svg viewBox="0 0 160 160" style={{ width: '100%', height: '100%', transform: 'rotate(-90deg)' }}>
                <circle cx="80" cy="80" r={radius} fill="none" stroke="var(--crm-border-light)" strokeWidth="18" />
                {statusItems.map((item: any, idx: number) => {
                  const pct = parseFloat(item.percentage) / 100;
                  const strokeDasharray = `${pct * circumference} ${circumference}`;
                  const strokeDashoffset = -accumulatedPercent * circumference;
                  accumulatedPercent += pct;

                  return (
                    <circle
                      key={idx}
                      cx="80"
                      cy="80"
                      r={radius}
                      fill="none"
                      stroke={item.color}
                      strokeWidth="18"
                      strokeDasharray={strokeDasharray}
                      strokeDashoffset={strokeDashoffset}
                      style={{ transition: 'stroke-dasharray 0.5s ease, stroke-dashoffset 0.5s ease' }}
                    />
                  );
                })}
              </svg>

              {/* Center Total overlay */}
              <div style={{
                position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'
              }}>
                <div style={{ fontSize: '20px', fontWeight: 800, color: 'var(--crm-text-primary)' }}>{reportData.totalLeads}</div>
                <div style={{ fontSize: '11px', color: 'var(--crm-text-muted)', textTransform: 'uppercase' }}>Leads</div>
              </div>
            </div>

            {/* Donut Legend */}
            <div style={{ flex: 1, minWidth: '150px' }}>
              {statusItems.map((item: any) => (
                <div key={item.status} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px', fontSize: '12.5px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: item.color }} />
                    <span style={{ color: 'var(--crm-text-primary)', fontWeight: 500 }}>{item.status}</span>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <span style={{ fontWeight: 700, color: 'var(--crm-text-primary)' }}>{item.count}</span>
                    <span style={{ color: 'var(--crm-text-muted)', width: '36px', textAlign: 'right' }}>({item.percentage}%)</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>

      {/* GRAPH SECTION 2: Lead Sources & Sales Rep Visual Bar Charts */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: '20px', marginTop: '20px' }}>

        {/* Graph 3: Color-Coded Lead Source Breakdown (Horizontal Bar Chart) */}
        <div className="crm-table-wrapper" style={{ padding: '20px', background: 'var(--crm-bg-card)', borderRadius: '12px' }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '15px', color: 'var(--crm-text-primary)' }}>
            🌐 Lead Volume by Channel / Source
          </h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {Object.entries(reportData.sourceBreakdown || {})
              .sort((a: any, b: any) => b[1] - a[1])
              .map(([src, count]: [string, any], idx: number) => {
                const color = SOURCE_COLORS[idx % SOURCE_COLORS.length];
                const pct = reportData.totalLeads ? Math.round((count / reportData.totalLeads) * 100) : 0;
                return (
                  <div key={src}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12.5px', marginBottom: '4px' }}>
                      <span style={{ fontWeight: 600, color: 'var(--crm-text-primary)' }}>{src}</span>
                      <span style={{ color: 'var(--crm-text-secondary)', fontWeight: 600 }}>{count} leads ({pct}%)</span>
                    </div>
                    <div style={{ height: '8px', width: '100%', backgroundColor: 'var(--crm-border-light)', borderRadius: '4px', overflow: 'hidden' }}>
                      <div style={{ width: `${pct}%`, height: '100%', backgroundColor: color, borderRadius: '4px', transition: 'width 0.5s ease' }} />
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      </div>

      {/* SECTION 3: Sales Representatives Rankings Table & Performance Comparison */}
      <div style={{ marginTop: '20px' }}>
        <div className="crm-table-wrapper">
          <div style={{ padding: '16px 20px', background: 'var(--crm-bg)', borderBottom: '1px solid var(--crm-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
            <h3 style={{ margin: 0, fontSize: '15px', color: 'var(--crm-text-primary)', fontWeight: 'bold' }}>
              📊 Sales Team Performance & Efficiency Leaderboard
            </h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '12.5px', color: 'var(--crm-text-secondary)' }}>Sort by:</span>
              <select 
                value={sortBy} 
                onChange={(e) => setSortBy(e.target.value as any)} 
                className="crm-select-input"
                style={{ padding: '4px 8px', fontSize: '12.5px', borderRadius: '4px', border: '1px solid var(--crm-border)' }}
              >
                <option value="efficiency">Efficiency Score (Conv + Speed)</option>
                <option value="conversion">Conversion Win Rate</option>
                <option value="speed">Response Speed</option>
                <option value="volume">Leads Assigned</option>
              </select>
            </div>
          </div>
          
          <table className="crm-table">
            <thead>
              <tr>
                <th style={{ width: '60px', textAlign: 'center' }}>Rank</th>
                <th>Representative</th>
                <th style={{ textAlign: 'center' }}>Leads Assigned</th>
                <th style={{ textAlign: 'center' }}>Contact Rate</th>
                <th>Win Rate (Conversion %)</th>
                <th style={{ textAlign: 'center' }}>Avg Response Speed</th>
                <th style={{ textAlign: 'center' }}>Score Rating</th>
              </tr>
            </thead>
            <tbody>
              {sortedPerformance.length > 0 ? sortedPerformance.map((rep: SalesRepStat, idx: number) => {
                let rankText = `${idx + 1}`;
                let rankStyle: React.CSSProperties = {
                  width: '28px', height: '28px', borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto', fontSize: '12px', fontWeight: 'bold'
                };
                
                if (idx === 0) {
                  rankText = '🥇';
                  rankStyle.background = '#FEF3C7';
                  rankStyle.fontSize = '16px';
                } else if (idx === 1) {
                  rankText = '🥈';
                  rankStyle.background = '#F3F4F6';
                  rankStyle.fontSize = '16px';
                } else if (idx === 2) {
                  rankText = '🥉';
                  rankStyle.background = '#FFE4E6';
                  rankStyle.fontSize = '16px';
                } else {
                  rankStyle.background = 'var(--crm-border-light)';
                  rankStyle.color = 'var(--crm-text-secondary)';
                }

                let speedText = '—';
                let speedColor = 'var(--crm-text-muted)';
                if (rep.totalAssigned > 0 && rep.contacted > 0) {
                  const t = rep.avgResponseTimeMin;
                  speedText = `${t} min`;
                  if (t <= 15) speedColor = 'var(--crm-success)';
                  else if (t <= 60) speedColor = 'var(--crm-info)';
                  else speedColor = 'var(--crm-danger)';
                }

                return (
                  <tr key={rep.repId}>
                    <td style={{ textAlign: 'center' }}>
                      <div style={rankStyle}>{rankText}</div>
                    </td>
                    <td style={{ fontWeight: 600 }}>{rep.repName}</td>
                    <td style={{ textAlign: 'center' }}>{rep.totalAssigned}</td>
                    <td style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '13px', fontWeight: 500 }}>
                        {rep.totalAssigned > 0 
                          ? `${Math.round((rep.contacted / rep.totalAssigned) * 100)}%` 
                          : '0%'}
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--crm-text-muted)' }}>
                        ({rep.contacted} of {rep.totalAssigned})
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{ width: '80px', height: '6px', background: 'var(--crm-border-light)', borderRadius: '3px', overflow: 'hidden' }}>
                          <div style={{ width: `${rep.conversionRate}%`, height: '100%', background: 'var(--crm-success)' }}></div>
                        </div>
                        <span style={{ fontWeight: 600, color: 'var(--crm-success)', fontSize: '13px' }}>
                          {rep.conversionRate}%
                        </span>
                        <span style={{ fontSize: '11px', color: 'var(--crm-text-muted)' }}>
                          ({rep.converted} leads)
                        </span>
                      </div>
                    </td>
                    <td style={{ textAlign: 'center', color: speedColor, fontWeight: 600 }}>
                      {speedText}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center' }}>
                        <span style={{ 
                          padding: '3px 8px', borderRadius: '12px', fontSize: '11.5px', fontWeight: 'bold',
                          background: rep.ratingBadge !== 'Standard' ? `${rep.ratingColor}20` : 'var(--crm-border-light)',
                          color: rep.ratingColor
                        }}>
                          {rep.ratingBadge}
                        </span>
                        <span style={{ fontSize: '10px', color: 'var(--crm-text-muted)', marginTop: '2px' }}>
                          Score: {rep.efficiencyScore}/100
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              }) : (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', padding: '30px', color: 'var(--crm-text-muted)' }}>
                    No sales representative performance details available.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
};

export default CRMReports;
